import { defineComponent } from 'vue'
import { mapActions, mapGetters, mapMutations } from 'vuex'
import { CONSENT_CATEGORIES, COOKIE_REGISTRY } from '@/constants/cookie-registry'
import consentToggle from './components/consent-toggle/index.vue'

const BANNER_CLASS = 'has-consent-banner'
const BANNER_HEIGHT_VAR = '--consent-banner-height'

const SCROLL_LOCK_CLASS = 'hide'

export default defineComponent({
  name: 'cookie-consent',
  components: {
    consentToggle,
  },
  data() {
    return {
      categories: CONSENT_CATEGORIES,
      bannerObserver: null,
    }
  },
  computed: {
    ...mapGetters({
      isConsentOpen: 'consent/isConsentOpen',
      isBannerVisible: 'consent/isBannerVisible',
      isSettingsVisible: 'consent/isSettingsVisible',
      draft: 'consent/consentDraft',
      cookieListRows: 'consentTexts/cookieListRows',
      cookieBannerHtml: 'consentTexts/cookieBannerHtml',
    }),

    // The published list when there is one, the registry otherwise. The two are not reconciled
    // on purpose: the published list is a legal document and its rows are shown exactly as it
    // states them - "_ga, _ga_7NKHN796HW" is one cell of text there, and splitting it to match
    // two registry entries would show rows the document does not contain. COOKIE_REGISTRY keeps
    // its own job either way, which is deleting cookies after a withdrawal - that needs `match`
    // and `firstParty`, which the published list has no room for.
    cookieRows() {
      if (this.cookieListRows) return this.cookieListRows

      return COOKIE_REGISTRY.map((cookie) => ({
        name: cookie.name,
        vendor: cookie.vendor,
        category: cookie.category,
        purpose: this.$t(cookie.purposeKey),
        retention: this.$t(cookie.retentionKey),
      }))
    },

    cookiesByCategory() {
      return this.cookieRows.reduce((acc, cookie) => {
        if (!acc[cookie.category]) acc[cookie.category] = []
        acc[cookie.category].push(cookie)
        return acc
      }, {})
    },
  },
  watch: {
    isSettingsVisible: {
      immediate: true,
      handler(visible) {
        this.toggleScrollLock(visible)
      },
    },
    isBannerVisible: {
      immediate: true,
      handler(visible) {
        this.toggleBannerClass(visible)
      },
    },
  },
  beforeDestroy() {
    this.toggleScrollLock(false)
    this.toggleBannerClass(false)
    this.stopBannerMeasure()
  },
  methods: {
    ...mapActions({
      acceptAll: 'consent/ACCEPT_ALL',
      rejectAll: 'consent/REJECT_ALL',
      saveCustom: 'consent/SAVE_CUSTOM',
      openSettings: 'consent/OPEN_SETTINGS',
      closeSettings: 'consent/CLOSE_SETTINGS',
    }),
    ...mapMutations({
      setDraftCategory: 'consent/SET_DRAFT_CATEGORY',
    }),

    htmlNode() {
      return document.getElementById('html')
    },

    toggleScrollLock(locked) {
      const node = this.htmlNode()
      if (!node) return
      node.classList[locked ? 'add' : 'remove'](SCROLL_LOCK_CLASS)
    },

    toggleBannerClass(visible) {
      const node = this.htmlNode()
      if (!node) return
      node.classList[visible ? 'add' : 'remove'](BANNER_CLASS)

      if (visible) {
        this.$nextTick(this.startBannerMeasure)
      } else {
        this.stopBannerMeasure()
      }
    },

    measureBanner() {
      const banner = this.$refs.banner
      const height = banner ? banner.offsetHeight : 0
      document.documentElement.style.setProperty(BANNER_HEIGHT_VAR, height + 'px')
    },

    startBannerMeasure() {
      this.measureBanner()

      if (typeof ResizeObserver === 'function' && this.$refs.banner) {
        this.bannerObserver = new ResizeObserver(this.measureBanner)
        this.bannerObserver.observe(this.$refs.banner)
      } else {
        window.addEventListener('resize', this.measureBanner)
      }
    },

    stopBannerMeasure() {
      if (this.bannerObserver) {
        this.bannerObserver.disconnect()
        this.bannerObserver = null
      } else {
        window.removeEventListener('resize', this.measureBanner)
      }

      document.documentElement.style.removeProperty(BANNER_HEIGHT_VAR)
    },

    isCategoryEnabled(category) {
      if (category.required) return true
      return Boolean(this.draft[category.key])
    },

    onToggleCategory(category, value) {
      if (category.required) return
      this.setDraftCategory({ key: category.key, value })
    },

    onAcceptAll(source) {
      this.acceptAll({ source })
    },

    onRejectAll(source) {
      this.rejectAll({ source })
    },

    onSaveCustom() {
      this.saveCustom({ source: 'settings' })
    },

    onOpenSettings(openedFrom) {
      this.openSettings({ openedFrom })
    },

    onCloseSettings() {
      this.closeSettings()
    },
  },
})
